<template>
  <v-card-text
    v-if="!loading"
  >
    <v-flex
      my-3
    >
      <p class="text-xs-center">
        {{ operation }}日時を設定してください。
      </p>
    </v-flex>
    <v-flex
      xs12
      px-3
    >
      <Chart
        ref="chart"
        :from="from"
        :from-records="fromRecords"
        :to="to"
        :to-records="toRecords"
        :replaced-at="range[index]"
        :sensor-installed-at="sensorInstalledAt"
      />
    </v-flex>
    <v-flex
      xs12
      px-3
    >
      <v-slider
        v-model="index"
        color="#004914"
        append-icon="mdi-arrow-right-drop-circle"
        prepend-icon="mdi-arrow-left-drop-circle"
        always-dirty
        :min="0"
        :max="range.length - 1"
        :tick-size="range.length"
        @click:append="toRight"
        @click:prepend="toLeft"
        @end="emitTiming"
      />
    </v-flex>
    <v-layout
      class="float-data"
    >
      <v-flex xs2>
        <DateInput
          :value="rangeStart"
          label="期間開始"
          :max="rangeEnd"
          :min="dateStart"
          @input="initData($event, 'start')"
        />
      </v-flex>
      <v-flex
        xs2
        ml-3
      >
        <DateInput
          :value="rangeEnd"
          label="期間終了"
          :min="rangeStart"
          @input="initData($event,'end')"
        />
      </v-flex>
      <v-flex
        xs4
        ml-4
        mt-2
      >
        <v-card
          flat
        >
          <v-card-text
            class="subheading text-xs-center py-2 text-no-wrap px-0"
            style="border: 1px solid currentColor"
          >
            <span class="body-2">交換日時：</span>
            {{ replacedAt }}
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-card-text>
  <v-card-text v-else>
    <div class="loading-box">
      <v-progress-circular
        indeterminate
        color="success"
        width="2"
        size="38"
      />
    </div>
  </v-card-text>
</template>

<script>
import * as moment from 'moment'
import { api } from '@/api'
import { DateInput } from '@/components/input'
import Chart from './Chart'

const max = moment()
const min = moment(max).add(-7, 'days')

export default {
  name: 'TimingInput',
  components: {
    DateInput,
    Chart
  },
  props: {
    from: {
      type: String,
      default: () => null
    },
    to: {
      type: String,
      default: () => null
    },
    serialRegex: {
      type: RegExp,
      required: true
    },
    operation: {
      type: String,
      required: true
    },
    sensorInstalledAt: {
      type: Number,
      default: () => null
    }
  },
  data () {
    return {
      loading: true,
      fromRecords: [],
      toRecords: [],
      index: 0,
      rangeStart: min.unix(),
      rangeEnd: max.unix()
    }
  },
  computed: {
    range () {
      const from = this.fromRecords.map(el => el.timestamp)
      const to = this.toRecords.map(el => el.timestamp)
      const set = new Set([...from, ...to])
      return Array.from(set).sort((a, b) => a - b)
    },
    timestamp () {
      return this.range[this.index]
    },
    replacedAt () {
      if (!this.timestamp) return '通信記録無し'
      return moment.unix(this.timestamp).format('MM/DD HH:mm')
    },
    dateStart () {
      if (this.sensorInstalledAt) {
        return moment.unix(this.sensorInstalledAt).startOf('day').unix()
      } else {
        return moment('2020-10-01').startOf('day').unix()
      }
    }
  },
  mounted () {
    this.initData()
  },
  methods: {
    async initData (dateString, caller) {
      this.loading = true
      this.fromRecords = []
      this.toRecords = []
      this.index = 0

      let rangeStart = this.rangeStart
      let rangeEnd = this.rangeEnd

      if (dateString && caller) {
        if (caller === 'start') {
          const timestamp = moment(dateString).startOf('day').unix()
          rangeStart = timestamp
        } else {
          const timestamp = moment(dateString).endOf('day').unix()
          rangeEnd = timestamp
        }
        // 取得期間が30日以上の場合30日にする
        const month = 2592000
        const monthAfterStart = rangeStart + month
        if (monthAfterStart < rangeEnd) {
          caller === 'start'
            ? rangeEnd = monthAfterStart
            : rangeStart = rangeEnd - month
        }
      }

      // 現センサー設置時刻以前のrecordsを表示しない
      if (rangeStart < this.dateStart) {
        rangeStart = this.dateStart
      }

      this.rangeStart = rangeStart
      this.rangeEnd = rangeEnd

      this.fromRecords = await this.getRecords(this.from)
      this.toRecords = await this.getRecords(this.to)
      this.emitTiming()

      this.loading = false
    },
    async getRecords (value) {
      if (!value) return []
      const { records } = await api.getSensor(value, {
        includes: 'records',
        rangeFrom: this.rangeStart,
        rangeTo: this.rangeEnd
      })
      return this.map(records)
    },
    map (records) {
      const l = []

      for (const r of records) {
        const span = r.meta.span / 4
        const ts = r.timestamp

        for (let i = 0; i < 4; i++) {
          const value = r.data[i]
          if (value === 1444 || value === 1555) {
            continue
          }

          const timestamp = ts - span * (4 - (i + 1))

          l.push({
            timestamp,
            value
          })
        }
      }
      l.sort((a, b) => {
        if (a.timestamp === b.timestamp) {
          return 0
        } else if (a.timestamp < b.timestamp) {
          return -1
        } else {
          return +1
        }
      })
      return l
    },
    toLeft () {
      if (this.range.length === 0) {
        return
      }
      this.index = Math.max(this.index - 1, 0)
      this.emitTiming()
    },
    toRight () {
      if (this.range.length === 0) {
        return
      }
      this.index = Math.min(this.range.length - 1, this.index + 1)
      this.emitTiming()
    },
    emitTiming () {
      this.$emit('updateTimestamp', this.timestamp)
    }
  }
}
</script>

<style lang="scss" scoped>
.float-data {
  position: absolute;
  bottom: 15px;
  left: 70px;
}
.loading-box {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 521px;
}
</style>
